/**
 * Accounting Services › Adjusting Entries: month-end journal entries posted to QuickBooks with
 * support attached, optional reversal on the first of the next month, and recurring schedules
 * (depreciation, prepaid and loan amortization) that come back each month.
 */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus, Repeat, Trash2 } from "lucide-react";
import { useState } from "react";
import { AccountSelect, MoneyInput, useAccounts, type Account, type ListRow } from "@/components/bookkeeping";
import { ask } from "@/components/dialogs";
import { Empty, Loading, Section } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatPeriod } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type Kind = "accrual" | "depreciation" | "prepaid" | "loan" | "reclass" | "other";
type EntryLine = { accountId: string; accountName: string; debit: number; credit: number; description: string; classId: string | null };
type Entry = {
  id: string;
  qboId: string;
  docNumber: string | null;
  txnDate: string;
  kind: Kind;
  memo: string;
  totalCents: number;
  lines: EntryLine[];
  reversalQboId: string | null;
  reversalDate: string | null;
  schedulePeriod: string | null;
  link: string;
  support: Array<{ id: string; filename: string }>;
};
type Schedule = {
  id: string;
  name: string;
  kind: Kind;
  memo: string;
  lines: EntryLine[];
  totalCents: number | null;
  dayOfMonth: number | null;
  startPeriod: string;
  months: number | null;
  nextPeriod: string;
  postedCount: number;
  active: boolean;
  due: string[];
  nextLines: EntryLine[];
};
type Data = { today: string; entries: Entry[]; schedules: Schedule[] };

export const KIND_LABEL: Record<Kind, string> = { accrual: "Accrual", depreciation: "Depreciation", prepaid: "Prepaid amortization", loan: "Loan amortization", reclass: "Reclassification", other: "Other adjustment" };
const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const blank = (): EntryLine => ({ accountId: "", accountName: "", debit: 0, credit: 0, description: "", classId: null });
const debits = (ls: EntryLine[]) => ls.reduce((a, l) => a + l.debit, 0);

export function AdjustingEntries({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/books/adjusting`], retry: false });
  const accounts = useAccounts(clientId).filter((a) => a.accountType !== "Accounts Receivable" && a.accountType !== "Accounts Payable");
  const canEdit = hasPermission(me, "engagements.write");
  const [dialog, setDialog] = useState<"entry" | "schedule" | null>(null);
  const post = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; period: string }) => api.send<Entry>("POST", `/api/recurring/${v.id}/post`, { period: v.period }),
    onSuccess: (e) => toast({ title: "Posted to QuickBooks", description: `${KIND_LABEL[e.kind]} entry dated ${formatDate(e.txnDate)} for ${formatCents(e.totalCents)}.` }),
  });
  const skip = useMutation({ meta: { inlineError: true }, mutationFn: (v: { id: string; period: string }) => api.send("POST", `/api/recurring/${v.id}/skip`, { period: v.period }) });
  const toggle = useMutation({ meta: { inlineError: true }, mutationFn: (v: { id: string; active: boolean }) => api.send("PATCH", `/api/recurring/${v.id}`, { active: v.active }) });
  const remove = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("DELETE", `/api/recurring/${id}`) });
  const attach = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; file: File }) => api.upload(`/api/adjusting/${v.id}/support`, {}, v.file),
    onSuccess: () => toast({ title: "Support attached", description: "Attached in QuickBooks and saved in the client's documents (Adjusting Entries)." }),
  });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const due = data.schedules.flatMap((s) => (s.due.length ? [{ s, period: s.due[0]! }] : []));
  const err = post.error ?? skip.error ?? toggle.error ?? remove.error ?? attach.error;
  return (
    <div className="space-y-6">
      <Section
        title="Due this month"
        description="Recurring entries whose next month has arrived. Post them before closing the month; months are posted in order."
        action={
          canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setDialog("schedule")}>
                <Repeat className="h-3.5 w-3.5" /> New recurring entry
              </Button>
              <Button size="sm" onClick={() => setDialog("entry")}>
                <Plus className="h-3.5 w-3.5" /> New adjusting entry
              </Button>
            </div>
          )
        }
      >
        {err && <p className="mb-3 text-sm text-destructive">{errorMessage(err)}</p>}
        {!due.length ? (
          <p className="text-sm text-muted-foreground">Nothing is due. {data.schedules.length ? "Every recurring entry is posted through this month." : "Add a recurring entry for depreciation, amortization or monthly accruals."}</p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {due.map(({ s, period }) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {KIND_LABEL[s.kind]} · {formatPeriod(period)}
                    {s.due.length > 1 && ` · ${s.due.length} months due`} · {s.nextLines.map((l) => `${l.debit ? "Dr" : "Cr"} ${l.accountName}`).join(", ")}
                  </p>
                </div>
                <span className="tabular font-medium">{formatCents(debits(s.nextLines))}</span>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="sm" disabled={post.isPending} onClick={() => post.mutate({ id: s.id, period })}>
                      Post {formatPeriod(period)}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={async () => (await ask({ title: `Skip ${formatPeriod(period)}?`, description: `Nothing is posted for ${s.name} this month. A skipped month doesn't count toward the schedule's length.`, confirmLabel: "Skip month" })) && skip.mutate({ id: s.id, period })}>
                      Skip
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Recurring entries" description="Each comes back every month from its first month until it ends or is stopped.">
        {!data.schedules.length ? (
          <Empty>No recurring entries.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {data.schedules.map((s) => (
              <li key={s.id} className={cn("flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm", !s.active && "text-muted-foreground")}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {s.name}
                    {!s.active && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px]">Stopped</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {KIND_LABEL[s.kind]} · {s.dayOfMonth ? `dated the ${s.dayOfMonth}${["th", "st", "nd", "rd"][s.dayOfMonth % 10 > 3 || [11, 12, 13].includes(s.dayOfMonth) ? 0 : s.dayOfMonth % 10]}` : "dated the last day"} · {s.months ? `${s.postedCount} of ${s.months} posted` : `${s.postedCount} posted, no end`}
                    {s.totalCents !== null && ` · ${formatCents(s.totalCents)} spread evenly`}
                    {s.active && (!s.months || s.postedCount < s.months) && ` · next ${formatPeriod(s.nextPeriod)}`}
                  </p>
                </div>
                <span className="tabular">{formatCents(debits(s.nextLines))} / month</span>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => toggle.mutate({ id: s.id, active: !s.active })}>
                      {s.active ? "Stop" : "Resume"}
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Delete ${s.name}`} onClick={async () => (await ask({ title: `Delete “${s.name}”?`, description: "Entries already posted stay in QuickBooks.", confirmLabel: "Delete", destructive: true })) && remove.mutate(s.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Posted entries" description="Adjusting entries posted from ClientFlow, newest first.">
        {!data.entries.length ? (
          <Empty>No adjusting entries have been posted from ClientFlow.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {data.entries.map((e) => (
              <li key={e.id} className="space-y-1 px-3 py-2.5 text-sm">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="tabular w-24 shrink-0">{formatDate(e.txnDate)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{e.memo}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {KIND_LABEL[e.kind]}
                      {e.docNumber && ` · #${e.docNumber}`}
                      {e.reversalDate && ` · reverses ${formatDate(e.reversalDate)}`}
                    </span>
                  </span>
                  <span className="tabular font-medium">{formatCents(e.totalCents)}</span>
                  <a href={e.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    QuickBooks <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-0 text-xs text-muted-foreground sm:pl-[6.75rem]">
                  <span>{e.lines.map((l) => `${l.debit ? "Dr" : "Cr"} ${l.accountName} ${formatCents(l.debit || l.credit)}`).join(" · ")}</span>
                  {e.support.map((d) => (
                    <a key={d.id} href={`/api/documents/${d.id}/download`} className="text-primary hover:underline">
                      {d.filename}
                    </a>
                  ))}
                  {canEdit && <UploadButton label={e.support.length ? "Attach more support" : "Attach support"} disabled={attach.isPending} onFile={(file) => attach.mutate({ id: e.id, file })} />}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {dialog && <EntryDialog clientId={clientId} accounts={accounts} mode={dialog} today={data.today} onClose={() => setDialog(null)} />}
    </div>
  );
}

/** A new adjusting entry, or a new recurring schedule (same lines, plus when it recurs). */
function EntryDialog({ clientId, accounts, mode, today, onClose }: { clientId: string; accounts: Account[]; mode: "entry" | "schedule"; today: string; onClose: () => void }) {
  const { toast } = useToast();
  const { data: classes } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/classes`], staleTime: 5 * 60_000, retry: false });
  const activeClasses = (classes ?? []).filter((c) => c.active);
  const lastOfPrior = new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, 0).toLocaleDateString("en-CA");
  const [kind, setKind] = useState<Kind>(mode === "schedule" ? "depreciation" : "accrual");
  const [date, setDate] = useState(lastOfPrior);
  const [docNumber, setDocNumber] = useState("");
  const [memo, setMemo] = useState("");
  const [reverse, setReverse] = useState(true);
  const [name, setName] = useState("");
  const [start, setStart] = useState(lastOfPrior.slice(0, 7));
  const [day, setDay] = useState<number | null>(null);
  const [months, setMonths] = useState("");
  const [spread, setSpread] = useState(false);
  const [total, setTotal] = useState(0);
  const [lines, setLines] = useState<EntryLine[]>([blank(), blank()]);
  const set = (i: number, patch: Partial<EntryLine>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const dr = debits(lines);
  const cr = lines.reduce((a, l) => a + l.credit, 0);
  const monthsN = months.trim() ? Number(months) : null;
  // A spread total uses two lines (debit and credit); their amounts come from the total.
  const spreadOk = spread && lines.length === 2 && total > 0 && !!monthsN && lines[0]!.accountId && lines[1]!.accountId;
  const balanced = spread ? !!spreadOk : dr > 0 && dr === cr && lines.every((l) => l.accountId && (l.debit > 0) !== (l.credit > 0));
  const [key] = useState(() => crypto.randomUUID());
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const clean = lines.map(({ accountName: _n, ...l }) => l);
      if (mode === "entry") return api.send("POST", `/api/clients/${clientId}/books/adjusting`, { date, docNumber: docNumber || undefined, kind, memo, lines: clean, reverse: kind === "accrual" && reverse }, key);
      const each = spread && monthsN ? Math.floor(total / monthsN) : 0;
      return api.send("POST", `/api/clients/${clientId}/books/recurring`, {
        name,
        kind,
        memo,
        // A spread schedule sends one month's amounts; the server works out each posting from the total.
        lines: spread ? clean.map((l, i) => (i === 0 ? { ...l, debit: each, credit: 0 } : { ...l, debit: 0, credit: each })) : clean,
        totalCents: spread ? total : null,
        dayOfMonth: day,
        startPeriod: start,
        months: monthsN,
      });
    },
    onSuccess: () => {
      toast({ title: mode === "entry" ? "Posted to QuickBooks" : "Recurring entry added", description: mode === "entry" ? undefined : "It appears under Due this month when its first month arrives." });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === "entry" ? "New adjusting entry" : "New recurring entry"}</DialogTitle>
          <DialogDescription>
            {mode === "entry" ? "Posted to QuickBooks as a journal entry. Attach the support after posting." : "Posted from Adjusting Entries each month, starting with its first month. Receivables and payables are posted as invoices and bills instead."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          {mode === "schedule" && (
            <label className="text-xs text-muted-foreground sm:col-span-2">
              <span className="mb-1 block">Name</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Depreciation — dental chairs" />
            </label>
          )}
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Type</span>
            <select className={cn(field, "w-full")} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
          {mode === "entry" ? (
            <>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Date</span>
                <input type="date" className={cn(field, "w-full")} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Entry number (optional)</span>
                <Input value={docNumber} maxLength={20} onChange={(e) => setDocNumber(e.target.value)} placeholder="AJE-1" />
              </label>
            </>
          ) : (
            <>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">First month</span>
                <input type="month" className={cn(field, "w-full")} value={start} onChange={(e) => e.target.value && setStart(e.target.value)} />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Dated</span>
                <select className={cn(field, "w-full")} value={day ?? ""} onChange={(e) => setDay(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">The last day of the month</option>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      Day {d}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="mb-1 block">Number of months (blank for no end)</span>
                <Input inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} placeholder="36" />
              </label>
            </>
          )}
          <label className="text-xs text-muted-foreground sm:col-span-3">
            <span className="mb-1 block">Memo</span>
            <Input value={memo} maxLength={1000} onChange={(e) => setMemo(e.target.value)} placeholder={mode === "entry" ? "Accrue October utilities, invoice received in November" : "Straight-line over 36 months"} />
          </label>
        </div>
        {mode === "entry" && kind === "accrual" && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={reverse} onChange={(e) => setReverse(e.target.checked)} /> Reverse on the first day of the next month
          </label>
        )}
        {mode === "schedule" && (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={spread}
                onChange={(e) => {
                  setSpread(e.target.checked);
                  // Spreading uses two lines whose amounts come from the total; turning it off clears them.
                  setLines((ls) => (e.target.checked ? [{ ...(ls[0] ?? blank()), debit: 1, credit: 0 }, { ...(ls[1] ?? blank()), debit: 0, credit: 1 }] : ls.map((l) => ({ ...l, debit: 0, credit: 0 }))));
                }}
              />
              Spread a total evenly over the months (prepaid expenses, loan fees)
            </label>
            {spread && (
              <span className="flex items-center gap-2">
                Total <span className="w-36"><MoneyInput label="Total to spread" value={total} onChange={setTotal} /></span>
                {monthsN && total > 0 ? <span className="text-xs text-muted-foreground">{formatCents(Math.floor(total / monthsN))} a month</span> : <span className="text-xs text-muted-foreground">Enter the number of months</span>}
              </span>
            )}
          </div>
        )}

        <div className="space-y-2">
          <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,2fr)_7.5rem_7.5rem_2rem] gap-2 px-1 text-xs font-medium text-muted-foreground sm:grid">
            <span>Account</span>
            <span>Description</span>
            <span className="text-right">Debit</span>
            <span className="text-right">Credit</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-md border border-border p-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_7.5rem_7.5rem_2rem] sm:border-0 sm:p-0">
              <div className="col-span-2 sm:col-span-1">
                <AccountSelect accounts={accounts} value={l.accountId} onChange={(v) => set(i, { accountId: v })} label={`Account, line ${i + 1}`} />
              </div>
              <div className="col-span-2 flex gap-2 sm:col-span-1">
                <Input aria-label={`Description, line ${i + 1}`} value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
                {activeClasses.length > 0 && (
                  <select aria-label={`Class, line ${i + 1}`} className={cn(field, "w-28 shrink-0")} value={l.classId ?? ""} onChange={(e) => set(i, { classId: e.target.value || null })}>
                    <option value="">No class</option>
                    {activeClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {spread ? (
                <span className="col-span-2 flex items-center justify-end text-xs text-muted-foreground sm:col-span-2">{i === 0 ? "Debit each month" : "Credit each month"}</span>
              ) : (
                <>
                  <MoneyInput label={`Debit, line ${i + 1}`} value={l.debit} onChange={(c) => set(i, { debit: c, ...(c ? { credit: 0 } : {}) })} />
                  <MoneyInput label={`Credit, line ${i + 1}`} value={l.credit} onChange={(c) => set(i, { credit: c, ...(c ? { debit: 0 } : {}) })} />
                </>
              )}
              <button type="button" aria-label={`Remove line ${i + 1}`} disabled={lines.length <= 2} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="flex items-center justify-center rounded text-muted-foreground hover:text-destructive disabled:opacity-30">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!spread && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2 text-sm">
              <Button size="sm" variant="ghost" onClick={() => setLines((ls) => [...ls, blank()])} disabled={lines.length >= 100}>
                <Plus className="h-3.5 w-3.5" /> Add line
              </Button>
              <span className={cn("tabular", dr !== cr ? "text-destructive" : "text-muted-foreground")}>
                Debits {formatCents(dr)} · Credits {formatCents(cr)}
                {dr !== cr && ` · off by ${formatCents(Math.abs(dr - cr))}`}
              </span>
            </div>
          )}
        </div>
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!balanced || save.isPending || (mode === "schedule" && !name.trim())}>
            {save.isPending ? "Saving…" : mode === "entry" ? "Post to QuickBooks" : "Add recurring entry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
